import { supabase } from '../lib/supabase';

// Utility function to handle refresh token errors
const handleRefreshTokenError = (error) => {
  if (error?.message?.includes('Invalid Refresh Token') || 
      error?.message?.includes('Refresh Token Not Found') ||
      error?.message?.includes('refresh_token_not_found')) {
    
    // Clear local storage tokens
    localStorage.removeItem('supabase.auth.token')
    localStorage.removeItem('sb-' + supabase?.supabaseUrl?.split('//')?.[1] + '-auth-token')
    
    return {
      isRefreshError: true,
      message: 'Your session has expired. Please sign in again.'
    }
  }
  return { isRefreshError: false }
}

export const authService = {
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        const refreshCheck = handleRefreshTokenError(error)
        if (refreshCheck?.isRefreshError) {
          throw new Error(refreshCheck.message)
        }
        throw error
      }
      
      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { 
          message: error?.message || 'Sign in failed. Please try again.' 
        } 
      };
    }
  },

  async signUp(email, password, fullName, role = 'employee', phone = '') {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
            phone: phone,
            avatar_url: ''
          }
        }
      });
      
      if (error) {
        const refreshCheck = handleRefreshTokenError(error)
        if (refreshCheck?.isRefreshError) {
          throw new Error(refreshCheck.message)
        }
        throw error
      }
      
      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { 
          message: error?.message || 'Sign up failed. Please try again.' 
        } 
      };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      
      // Always clear tokens regardless of response
      localStorage.removeItem('supabase.auth.token')
      localStorage.removeItem('sb-' + supabase?.supabaseUrl?.split('//')?.[1] + '-auth-token')
      
      if (error && !error?.message?.includes('Invalid Refresh Token')) {
        throw error
      }
      
      return { error: null };
    } catch (error) {
      // Don't throw error for refresh token issues during signout
      const refreshCheck = handleRefreshTokenError(error)
      if (refreshCheck?.isRefreshError) {
        return { error: null } // Successful cleanup
      }
      
      return { 
        error: { 
          message: error?.message || 'Sign out failed. Please try again.' 
        } 
      };
    }
  },

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      
      if (error) {
        const refreshCheck = handleRefreshTokenError(error)
        if (refreshCheck?.isRefreshError) {
          return { user: null, error: null } // Clean state
        }
        throw error
      }
      
      return { user, error: null };
    } catch (error) {
      return { 
        user: null, 
        error: { 
          message: error?.message || 'Failed to get current user.' 
        } 
      };
    }
  },

  async updateProfile(updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('No user logged in');

      // Update user profile in user_profiles table
      const { data, error } = await supabase?.from('user_profiles')?.update({
          full_name: updates?.fullName,
          phone: updates?.phone,
          address: updates?.address,
          avatar_url: updates?.avatarUrl,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id)?.select()?.single();

      if (error) {
        const refreshCheck = handleRefreshTokenError(error)
        if (refreshCheck?.isRefreshError) {
          throw new Error(refreshCheck.message)
        }
        throw error
      }
      
      return { data, error: null };
    } catch (error) {
      return { 
        data: null, 
        error: { 
          message: error?.message || 'Profile update failed. Please try again.' 
        } 
      };
    }
  },

  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });
      
      if (error) {
        const refreshCheck = handleRefreshTokenError(error)
        if (refreshCheck?.isRefreshError) {
          throw new Error(refreshCheck.message)
        }
        throw error
      }
      
      return { error: null };
    } catch (error) {
      return { 
        error: { 
          message: error?.message || 'Password reset failed. Please try again.' 
        } 
      };
    }
  },

  // New method to handle session recovery
  async refreshSession() {
    try {
      const { data, error } = await supabase?.auth?.refreshSession()
      
      if (error) {
        const refreshCheck = handleRefreshTokenError(error)
        if (refreshCheck?.isRefreshError) {
          return { 
            data: null, 
            error: { message: refreshCheck?.message }
          };
        }
        throw error
      }
      
      return { data, error: null }
    } catch (error) {
      return {
        data: null,
        error: { 
          message: error?.message || 'Session refresh failed. Please sign in again.'
        }
      }
    }
  },

  // Clear session method for manual cleanup
  clearSession() {
    localStorage.removeItem('supabase.auth.token')
    localStorage.removeItem('sb-' + supabase?.supabaseUrl?.split('//')?.[1] + '-auth-token')
  }
};

export default authService;